import React, { useState } from "react";
import { Box, Button, Radio, RadioGroup, Typography } from "@mui/material";
import LockOutlined from "@mui/icons-material/LockOutlined";

import RpMUIDialog from "@/components/modals/RpMUIModal";

type ResetPasswordMethod = "temporary_password" | "link";

const RESET_PASSWORD_METHODS = [
  {
    value: "temporary_password" as const,
    title: "Auto-generated password",
    description:
      "A secure password is generated and sent directly to the user's email. They'll be prompted to change it on next login.",
  },
  {
    value: "link" as const,
    title: "Send reset password link",
    description:
      "Send a secure password reset email to this user. Their current password will remain active until they create a new one.",
  },
];

type ResetPasswordModalProps = {
  open: boolean;
  userEmail?: string;
  onClose: () => void;
  onSubmit: (_mode: ResetPasswordMethod) => void;
};

const ResetPasswordModal = ({
  open,
  userEmail,
  onClose,
  onSubmit,
}: ResetPasswordModalProps) => {
  const [mode, setMode] = useState<ResetPasswordMethod>(
    RESET_PASSWORD_METHODS[0].value
  );

  return (
    <RpMUIDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      persists
      PaperProps={{
        sx: {
          borderRadius: "var(--radius-container)",
          maxWidth: 680,
          mx: { xs: 1.5, sm: "auto" },
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 620,
          mx: "auto",
          px: { xs: 1.5, sm: 5 },
          py: { xs: 2.5, sm: 5 },
        }}
      >
        <Box className="d-flex align-items-center mb-4">
          <Box
            className="d-flex align-items-center justify-content-center mr-2"
            sx={{
              width: 45,
              height: 45,
              borderRadius: "var(--radius-container)",
              backgroundColor: "var(--primary-tint)",
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            <LockOutlined fontSize="small" />
          </Box>
          <Typography
            variant="h6"
            className="widget-title mb-0"
            sx={{
              borderLeft: 0,
              color: "var(--neutral-700)",
              fontWeight: 700,
              pl: 0,
            }}
          >
            Invite Sub-User
          </Typography>
        </Box>

        <Typography
          className="mb-4"
          sx={{
            color: "var(--neutral-700)",
            fontSize: 14,
            lineHeight: "26px",
          }}
        >
          Choose how you&apos;d like to set up access for{" "}
          <Box
            component="strong"
            sx={{ color: "var(--neutral-700)", fontWeight: 700 }}
          >
            {userEmail || "this user"}
          </Box>{" "}
          The user will receive an email invitation and will be required to log
          in using the provided credentials.
        </Typography>

        <Typography
          className="fw-sb mb-2 text-uppercase"
          sx={{
            color: "var(--neutral-700)",
            fontSize: 13,
            fontWeight: 700,
            ml: 2.5,
          }}
        >
          Select Method
        </Typography>

        <RadioGroup
          value={mode}
          onChange={(event) =>
            setMode(event.target.value as ResetPasswordMethod)
          }
          sx={{ maxWidth: 500, mx: "auto" }}
        >
          {RESET_PASSWORD_METHODS.map((option) => (
            <Box
              key={option.value}
              component="label"
              className="d-flex align-items-start cursor-pointer mb-3"
              sx={{
                border: "1px solid",
                borderColor:
                  mode === option.value ? "primary.main" : "var(--neutral-500)",
                borderRadius: "var(--radius-control)",
                backgroundColor:
                  mode === option.value
                    ? "var(--primary-tint)"
                    : "var(--neutral-white)",
                px: 2,
                py: 1.5,
                transition:
                  "background-color 150ms ease, border-color 150ms ease",
              }}
            >
              <Radio
                checked={mode === option.value}
                value={option.value}
                color="primary"
                sx={{
                  color: "var(--neutral-500)",
                  "& .MuiSvgIcon-root": { fontSize: 24 },
                  p: 0,
                  mr: 2,
                  mt: 0.5,
                }}
              />
              <Box>
                <Typography
                  className="fw-sb mb-1"
                  sx={{ color: "var(--neutral-700)", fontWeight: 700 }}
                >
                  {option.title}
                </Typography>
                <Typography
                  className="text14P_Reg626262"
                  sx={{
                    color:
                      mode === option.value
                        ? "var(--neutral-700)"
                        : "var(--neutral-600)",
                    fontSize: 12,
                    lineHeight: "26px",
                  }}
                >
                  {option.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </RadioGroup>

        <Button
          variant="contained"
          fullWidth
          className="fw-sb mt-3"
          onClick={() => onSubmit(mode)}
          sx={{
            borderRadius: "var(--radius-control)",
            boxShadow: "none",
            display: "flex",
            maxWidth: 500,
            mx: "auto",
            py: 1.5,
          }}
        >
          Send invitation
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          className="text-capitalize mt-3"
          onClick={onClose}
          sx={{
            borderColor: "var(--neutral-500)",
            borderRadius: "var(--radius-control)",
            color: "var(--palette-text-primary)",
            display: "flex",
            maxWidth: 500,
            mx: "auto",
            py: 1.5,
            "&:hover": {
              borderColor: "var(--neutral-500)",
              backgroundColor: "var(--neutral-200)",
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    </RpMUIDialog>
  );
};

export default ResetPasswordModal;
